"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import classNames from "classnames";

// 1. Store position globally outside the component.
let persistedCursorPos = { x: -100, y: -100 };

export default function CustomCursor() {
  const ringRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  
  // 2. Initialize refs with the globally persisted position
  const cursor = useRef({ ...persistedCursorPos });
  const ring = useRef({ ...persistedCursorPos });
  
  const [isHovering, setIsHovering] = useState(false);
  const [isGrabbing, setIsGrabbing] = useState(false); // Track holding state
  const isSticky = useRef(false);

  // Combine both states to trigger the animation
  const isActive = isHovering || isGrabbing;

  // 3. Apply the saved position instantly before the browser repaints
  useLayoutEffect(() => {
    if (ringRef.current && dotRef.current && persistedCursorPos.x !== -100) {
      ringRef.current.style.transform = `translate(${persistedCursorPos.x}px, ${persistedCursorPos.y}px) translate(-50%, -50%)`;
      dotRef.current.style.transform = `translate(${persistedCursorPos.x}px, ${persistedCursorPos.y}px) translate(-50%, -50%)`;
    }
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      // 4. Update the global variable on every move
      persistedCursorPos = { x: e.clientX, y: e.clientY };
      cursor.current = { x: e.clientX, y: e.clientY };
      
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;
      }
    };
    
    const onMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInteractive = target.closest("a, button, input, [role='button'], .custom-cursor-hover");
      setIsHovering(!!isInteractive);
    };

    const onMouseDown = () => setIsGrabbing(true);
    const onMouseUp = () => setIsGrabbing(false);

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseover", onMouseOver);
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup", onMouseUp);
    
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseover", onMouseOver);
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (isHovering) {
      timeoutId = setTimeout(() => {
        isSticky.current = true;
      }, 50);
    } else {
      isSticky.current = false;
    }
    return () => clearTimeout(timeoutId);
  }, [isHovering]);

  useEffect(() => {
    let requestRef: number;
    
    const loop = () => {
      const factor = isSticky.current ? 0.9 : 0.15;
      
      ring.current.x += (cursor.current.x - ring.current.x) * factor;
      ring.current.y += (cursor.current.y - ring.current.y) * factor;
      
      if (ringRef.current) {
        ringRef.current.style.transform = `translate(${ring.current.x}px, ${ring.current.y}px) translate(-50%, -50%)`;
      }
      
      requestRef = requestAnimationFrame(loop);
    };
    
    loop();
    return () => cancelAnimationFrame(requestRef);
  }, []);

  return (
    <>
      
      <div
        data-comment="cursor outer ring"
        ref={ringRef}
        className={classNames(
          "hidden xl:block fixed top-0 left-0 rounded-full border-red bg-transparent pointer-events-none transition-[width,height,border-width] duration-200 ease",
          isActive ? "size-4 border-2" : "size-7 border-3"
        )}
        style={{ zIndex: 9999, transform: "translate(-100px, -100px)" }}
      />

      <div
        data-comment="cursor inner dot"
        ref={dotRef}
        className={classNames(
          "hidden xl:block fixed top-0 left-0 size-1.5 rounded-full bg-red pointer-events-none transition-[width,height] duration-200 ease",
          isActive ? "size-4" : "size-2"
        )}
        style={{ zIndex: 9999, transform: "translate(-100px, -100px)" }}
      />    
    </>
  );
}